import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { MeasurementResults } from '../../hooks/useResultsExport';
import { findCeIndicator } from './ceIndicatorGroups';
import CeVitalGaugeRow from './CeVitalGaugeRow';
import CeIndicatorDescription from './CeIndicatorDescription';
import Logo from '../Logo';
import FooterLogo from '../FooterLogo';
import { colors, fontSize, fontWeight } from '../../style/tokens';
import { headerBg } from '../../style/header';

interface CeIndicatorDetailPageProps {
  /** resultCode of the tapped indicator (matches the content API `code`). */
  code: string;
  results: MeasurementResults;
  onBack: () => void;
}

// ── Layout (mirrors MetricDetailPage) ───────────────────────────────────────────

const Page = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: var(--sfondo, ${colors.pageBg});
  font-family: 'Outfit', sans-serif;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px 26px;
  min-height: 82px;
  background: ${headerBg};
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
  position: relative;
`;

const BackButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1.5px solid ${p => p.theme.coloreBottoneInattivo || colors.border};
  background: ${colors.bg};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
  @media (hover: hover) { &:hover { background: ${p => p.theme.coloreBottoneAttivo || '#f5f5f5'}; border-color: transparent; } }
  &::before {
    content: '';
    display: block;
    width: 9px;
    height: 9px;
    border-top: 2px solid #333;
    border-left: 2px solid #333;
    transform: rotate(-45deg) translate(2px, 0);
  }
`;

const LogoCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`;

const Spacer = styled.span`width: 42px;`;

const Hero = styled.div`
  background: ${({ theme }) => theme.coloreSfondoIndicatore || theme.gradient1};
  padding: 20px 24px 22px;
  flex-shrink: 0;
`;

const HeroTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  line-height: 1.15;
`;

const HeroSub = styled.p`
  font-size: ${fontSize.base};
  color: rgba(255, 255, 255, 0.82);
  padding-left: 2px;
  margin-top: 6px;
`;

// ── Gauge bar (copy of the tapped report row) ────────────────────────────────────

const GaugeBar = styled.div`
  padding: 10px 24px 6px;
  background: ${colors.bg};
  border-bottom: 1px solid #f0f0f0;
`;

const BtnRow = styled.div`
  padding: 20px 14px 24px;
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

const BtnGhost = styled.button`
  flex: 1;
  padding: 15px 0;
  border-radius: 50px;
  border: 1.5px solid ${p => p.theme.coloreBottoneInattivo || '#ddd'};
  background: ${colors.bg};
  font-size: 15px;
  font-weight: ${fontWeight.medium};
  font-family: 'Outfit', sans-serif;
  color: ${p => p.theme.coloreBottoneInattivo || '#555'};
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  @media (hover: hover) {
    &:hover {
      background: ${p => p.theme.coloreBottoneAttivo || '#f9f9f9'};
      border-color: transparent;
      color: white;
    }
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const CeIndicatorDetailPage: React.FC<CeIndicatorDetailPageProps> = ({
  code,
  results,
  onBack,
}) => {
  const intl = useIntl();

  const found = findCeIndicator(code);

  // Defensive: an unknown code should never reach here, but never crash the app.
  if (!found) {
    return (
      <Page>
        <TopBar>
          <BackButton onClick={onBack} aria-label="Back to report" />
          <LogoCenter><Logo /></LogoCenter>
          <Spacer />
        </TopBar>
        <BtnRow>
          <BtnGhost onClick={onBack}>
            {intl.formatMessage({ id: 'metric.ui.backToReport' })}
          </BtnGhost>
        </BtnRow>
        <FooterLogo />
      </Page>
    );
  }

  const { group, indicator } = found;
  const vs = results.vitalSigns;
  const label = intl.formatMessage({ id: indicator.labelKey });
  const { display } = indicator.getValue(vs);
  const available = indicator.isAvailable(vs);
  const score = indicator.getScore ? indicator.getScore(vs, results.demographics) : 50;
  const lowConfidence = available && !!indicator.isLowConfidence?.(vs);

  return (
    <Page>
      <TopBar>
        <BackButton onClick={onBack} aria-label="Back to report" />
        <LogoCenter><Logo /></LogoCenter>
        <Spacer />
      </TopBar>

      <Hero>
        <HeroTitle>{label}</HeroTitle>
        {available && !indicator.hideValue
          ? <HeroSub>{display}</HeroSub>
          : (!available && <HeroSub>{intl.formatMessage({ id: 'metric.ui.unavailable' })}</HeroSub>)}
      </Hero>

      <GaugeBar>
        <CeVitalGaugeRow
          label={label}
          value={display}
          score={score}
          bands={group.bands}
          hideValue={indicator.hideValue}
          unavailable={!available}
          lowConfidence={lowConfidence}
          legend={indicator.legend}
          invertGaugeLabels={indicator.invertGaugeLabels}
        />
      </GaugeBar>

      <CeIndicatorDescription resultCode={indicator.resultCode} />

      <BtnRow>
        <BtnGhost onClick={onBack}>
          {intl.formatMessage({ id: 'metric.ui.backToReport' })}
        </BtnGhost>
      </BtnRow>

      <FooterLogo />
    </Page>
  );
};

export default CeIndicatorDetailPage;
